import React, { useCallback, useEffect, useState } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Grid, Typography, Box, TablePagination } from "@mui/material";
import {
  deleteProduct,
  getProductsError,
  getProductsStatus,
  selectAllProducts,
  selectProductsPagination,
  selectProductsLastParams,
  selectProductsQuery,
  setProductsQuery,
  resetProductsQuery,
  getProductsSuccess,
  updateStatus,
  getProductsPaginated,
} from "../../../features/productSlice";
import Loading from "../../../components/loading";
import Error from "../../../components/Error";
import ProductCard from "../../../components/card";
import NoData from "../../../components/noData";
import AppBarSearch from "../../../global/appBarSearch";
import AlertDialog from "../../../components/alertDialog";
import SetDiscountDialog from "./setDiscount";
import { toast } from "react-toastify";
import { useRestaurant } from "../../../hooks/RestaurantContext";
import FiltersDialog, {
  FiltersButton,
} from "../../../components/filters/filtersDialog";
import FilterChipGroup from "../../../components/filters/filterChipGroup";
import { StockList, VisibilityList } from "../../../models/product.model";
import {
  NamePriceSortList,
  DiscountFilterList,
  applySort,
} from "../../../models/sort.model";
import {
  fetchCategories,
  selectAllCategories,
} from "../../../features/categorySlice";
import { ellipsisText } from "../../../global/functions/textFormat";
import ParagraphHighliter from "../../../components/paragraphHighliter";
import { ResponsiveProvider } from "../../../hooks/uiHook";
import ShowMultiItems from "../../../components/showMultiItems";
import { CardField, CardPrice, CardStatus } from "../../../components/cardMeta";
import ProductDetailsModal from "../../../components/productDetailsModal";
import ListViewProducts from "./listViewProducts";
import { useIsMobile, useIsCompactHeader } from "../../../hooks/uiHook";
import { useTranslation } from "react-i18next";

const ProductsList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const isCompactHeader = useIsCompactHeader();
  const { restaurantId, currency } = useRestaurant();
  const [visibilityFilter, setVisibilityFilter] = useState("");
  const [stockFilter, setStockFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [discountFilter, setDiscountFilter] = useState("all");
  const [sortFilter, setSortFilter] = useState("");
  const [filtersDialogOpen, setFiltersDialogOpen] = useState(false);
  // Draft copies, bound to the FiltersDialog controls only: nothing here
  // affects the list until Appliquer commits it into the state above.
  const [draftVisibilityFilter, setDraftVisibilityFilter] = useState("");
  const [draftStockFilter, setDraftStockFilter] = useState("");
  const [draftCategoryFilter, setDraftCategoryFilter] = useState([]);
  const [draftDiscountFilter, setDraftDiscountFilter] = useState("all");
  const [draftSortFilter, setDraftSortFilter] = useState("");
  const categories = useSelector(selectAllCategories);
  const query = useSelector(selectProductsQuery);
  const { search, limit } = query;
  const page = query.page - 1;
  const [open, setOpen] = useState(false);
  const [cardId, setCardId] = useState(null);
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsProduct, setDetailsProduct] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const status = useSelector(getProductsStatus);
  const error = useSelector(getProductsError);
  const products = useSelector(selectAllProducts);
  const pagination = useSelector(selectProductsPagination);
  const lastFetchedParams = useSelector(selectProductsLastParams);
  const success = useSelector(getProductsSuccess);
  const totalCount = pagination.totalRecords || 0;

  const handleClickOpen = (cardId) => {
    setOpen(true);
    setCardId(cardId);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    navigate("add");
  };

  const handleModify = (data) => {
    navigate("modify", {
      state: {
        product: data,
      },
    });
  };

  const handleDiscount = (product) => {
    setSelectedProduct(product);
    setDiscountDialogOpen(true);
  };

  const handleViewDetails = (product) => {
    setDetailsProduct(product);
    setDetailsOpen(true);
  };

  // Reset the search/pagination query on every fresh mount (not just on
  // restaurant switch, handled separately below) so navigating away (e.g. to
  // Add/Modify, which unmounts this whole screen — see src/App.js's sibling
  // <Route>s) and back always starts from a clean search, consistent with
  // Utilisateurs/Commandes where search is local state that resets in full.
  // Placed above the fetch effect so it's registered (and runs) first.
  useEffect(() => {
    dispatch(resetProductsQuery());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let effectiveSearch = search;
    let effectiveLimit = limit;
    let effectivePage = page + 1;

    if (lastFetchedParams) {
      let previousRestaurantId;
      try {
        previousRestaurantId = JSON.parse(lastFetchedParams).restaurantId;
      } catch {
        previousRestaurantId = undefined;
      }
      if (previousRestaurantId !== restaurantId) {
        // Restaurant changed since the last fetch: start that restaurant's
        // list fresh instead of reusing another restaurant's search/page.
        effectiveSearch = "";
        effectiveLimit = 12;
        effectivePage = 1;
        dispatch(resetProductsQuery());
      }
    }

    const params = {
      restaurantId,
      search: effectiveSearch,
      limit: effectiveLimit,
      page: effectivePage,
    };
    const serialized = JSON.stringify(params);
    if (lastFetchedParams === serialized && status === "fetchDataPaginated") {
      return;
    }
    dispatch(getProductsPaginated(params));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, restaurantId, search, limit, page]);

  // Full (unpaginated) category list, only used to populate the Catégorie
  // chip filter below — mirrors how addProduct.jsx loads it for its form.
  useEffect(() => {
    dispatch(fetchCategories(restaurantId));
  }, [dispatch, restaurantId]);

  const hasDiscount = (product) =>
    !!product.discountValue && product.discountValue > 0;

  // Order-insensitive array compare for categoryFilter/draftCategoryFilter
  // (chip toggling can produce the same set of ids in a different order).
  const arraysEqualUnordered = (a, b) => {
    const sortedA = [...(a || [])].map(String).sort();
    const sortedB = [...(b || [])].map(String).sort();
    return JSON.stringify(sortedA) === JSON.stringify(sortedB);
  };

  // Live draft-vs-applied diff: Appliquer stays disabled until at least one
  // draft filter differs from what's currently applied, and re-disables
  // itself automatically if the user changes a filter back to match.
  const hasPendingChanges =
    draftVisibilityFilter !== visibilityFilter ||
    draftStockFilter !== stockFilter ||
    !arraysEqualUnordered(draftCategoryFilter, categoryFilter) ||
    draftDiscountFilter !== discountFilter ||
    draftSortFilter !== sortFilter;

  // Visibility/stock/category/discount are not (yet) supported as query
  // params by the backend /product endpoint, so they're applied client-side
  // on the current page, then sorted, same approach as the existing filters.
  const filteredProducts = applySort(
    (products || []).filter((product) => {
      if (visibilityFilter !== "" && String(product.visible) !== String(visibilityFilter)) {
        return false;
      }
      if (stockFilter !== "" && String(product.outOfStock) !== String(stockFilter)) {
        return false;
      }
      if (categoryFilter.length > 0) {
        const productCategoryIds = (product.categories || []).map((category) =>
          String(category?._id ?? category)
        );
        if (!categoryFilter.some((id) => productCategoryIds.includes(String(id)))) {
          return false;
        }
      }
      if (discountFilter === "with" && !hasDiscount(product)) return false;
      if (discountFilter === "without" && hasDiscount(product)) return false;
      return true;
    }),
    sortFilter
  );

  useEffect(() => {
    if (status === "deleteSuccess") {
      toast.success(success);
      dispatch(updateStatus());
    } else if (status === "deleteError") {
      toast.error(error);
    } else if (
      status === "discountSetSuccess" ||
      status === "discountRemoveSuccess"
    ) {
      dispatch(updateStatus());
    }
  }, [dispatch, error, status, success]);

  const handleChangePage = useCallback(
    (event, newPage) => {
      const maxPage = Math.max(0, Math.ceil(totalCount / limit) - 1);
      const clampedPage = Math.min(newPage, maxPage);
      dispatch(setProductsQuery({ page: clampedPage + 1 }));
    },
    [dispatch, limit, totalCount]
  );

  const handleChangeRowsPerPage = useCallback(
    (event) => {
      const newLimit = Number(event.target.value);
      dispatch(setProductsQuery({ limit: newLimit, page: 1 }));
    },
    [dispatch]
  );

  const effectiveViewMode = isCompactHeader ? "grid" : viewMode;

  return (
    <div className="main-application">
      <CssBaseline />
      <ResponsiveProvider maxWidth={900}>
        <AppBarSearch
          disableMobileDrawer
          searchText={t("search.byNameOrDescription")}
          handleSubmit={handleSubmit}
          handleSearch={(e) => {
            dispatch(setProductsQuery({ search: e.target.value, page: 1 }));
          }}
          title={t("pages.myProducts")}
          buttonTitle={t("actions.addProduct")}
          viewMode={effectiveViewMode}
          onToggleViewMode={
            isCompactHeader
              ? undefined
              : () => setViewMode(viewMode === "list" ? "grid" : "list")
          }
          children={
            <>
              <FiltersButton
                onClick={() => {
                  setDraftVisibilityFilter(visibilityFilter);
                  setDraftStockFilter(stockFilter);
                  setDraftCategoryFilter(categoryFilter);
                  setDraftDiscountFilter(discountFilter);
                  setDraftSortFilter(sortFilter);
                  setFiltersDialogOpen(true);
                }}
                activeCount={
                  (visibilityFilter !== "" ? 1 : 0) +
                  (stockFilter !== "" ? 1 : 0) +
                  categoryFilter.length +
                  (discountFilter !== "all" ? 1 : 0) +
                  (sortFilter ? 1 : 0)
                }
              />
            </>
          }
        />
      </ResponsiveProvider>
      <FiltersDialog
        open={filtersDialogOpen}
        onClose={() => setFiltersDialogOpen(false)}
        onReset={() => {
          setDraftVisibilityFilter("");
          setVisibilityFilter("");
          setDraftStockFilter("");
          setStockFilter("");
          setDraftCategoryFilter([]);
          setCategoryFilter([]);
          setDraftDiscountFilter("all");
          setDiscountFilter("all");
          setDraftSortFilter("");
          setSortFilter("");
          setFiltersDialogOpen(false);
        }}
        onApply={() => {
          setVisibilityFilter(draftVisibilityFilter);
          setStockFilter(draftStockFilter);
          setCategoryFilter(draftCategoryFilter);
          setDiscountFilter(draftDiscountFilter);
          setSortFilter(draftSortFilter);
          setFiltersDialogOpen(false);
        }}
        disableApply={!hasPendingChanges}
      >
        <FilterChipGroup
          label={t("filters.allVisibilities")}
          options={VisibilityList}
          value={draftVisibilityFilter}
          onChange={setDraftVisibilityFilter}
          allowAll
        />
        <FilterChipGroup
          label={t("filters.allStocks")}
          options={StockList}
          value={draftStockFilter}
          onChange={setDraftStockFilter}
          allowAll
        />
        <FilterChipGroup
          label={t("filters.category")}
          options={(categories || []).map((category) => ({
            _id: category._id,
            label: category.name,
          }))}
          value={draftCategoryFilter}
          onChange={setDraftCategoryFilter}
          multiple
        />
        <FilterChipGroup
          label={t("common.discount")}
          options={DiscountFilterList}
          value={draftDiscountFilter}
          onChange={setDraftDiscountFilter}
        />
        <FilterChipGroup
          label={t("filters.sortBy")}
          options={NamePriceSortList}
          value={draftSortFilter}
          onChange={setDraftSortFilter}
        />
      </FiltersDialog>
      <main>
        <Box sx={{ my: 2 }}>
          {effectiveViewMode === "list" ? (
            <ListViewProducts
              products={filteredProducts}
              page={page}
              limit={limit}
              handleClickOpen={handleClickOpen}
              handleModify={handleModify}
              handleDiscount={handleDiscount}
              handleViewDetails={handleViewDetails}
              status={status}
              error={error}
              search={search}
            />
          ) : (
          <Grid container spacing={2}>
            {status === "loadingFetchDataPaginated" ? (
              <Loading size={9} />
            ) : status === "fetchErrorPaginated" ? (
              <Error>{error}</Error>
            ) : (
              <>
                {filteredProducts && filteredProducts.length > 0 ? (
                  filteredProducts.map((card) => (
                    <ProductCard
                      key={card._id}
                      data={card}
                      handleModify={() => handleModify(card)}
                      handleDiscount={handleDiscount}
                      onViewDetails={handleViewDetails}
                      search={search}
                      content={
                        <>
                          {card.categories && (
                            <ShowMultiItems items={card.categories} text={t("fields.categoriesLabel")} />
                          )}
                          <CardPrice
                            label={t("fields.price")}
                            price={
                              card.discountValue && card.discountValue > 0
                                ? card.discountedPrice
                                  ? card.discountedPrice.toFixed(2)
                                  : (card.price - card.discountValue).toFixed(2)
                                : card?.price ?? 0
                            }
                            oldPrice={
                              card.discountValue && card.discountValue > 0
                                ? card.price
                                : null
                            }
                            currency={currency}
                          />
                          {!isMobile && (
                            <CardField label={t("fields.formulePrice")}>
                              {card?.formulePrice ?? 0} {currency}
                            </CardField>
                          )}
                          {!isMobile && card.tva > 0 && (
                            <CardField label={t("fields.tva")}>{card?.tva}</CardField>
                          )}
                          {!isMobile && card.type?.length > 0 && (
                            <ShowMultiItems items={card.type} text={t("fields.optionsLabel")} />
                          )}
                          {card.description && card.description !== "" && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={
                                isMobile
                                  ? {
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }
                                  : undefined
                              }
                            >
                              {t("fields.description")}:{" "}
                              <ParagraphHighliter
                                title={isMobile ? card?.description : ellipsisText(card?.description, 80)}
                                search={search}
                              />
                            </Typography>
                          )}
                          <CardStatus
                            outOfStock={card.outOfStock}
                            visible={card.visible}
                          />
                        </>
                      }
                      handleClickOpen={() => handleClickOpen(card._id)}
                    />
                  ))
                ) : (
                  <NoData />
                )}
              </>
            )}
          </Grid>
          )}
          <AlertDialog
            handleClose={() => setOpen(false)}
            open={open}
            name={"produit"}
            cardId={cardId}
            deleteData={() =>
              dispatch(deleteProduct({ productId: cardId, restaurantId }))
            }
          />
          <SetDiscountDialog
            open={discountDialogOpen}
            onClose={() => {
              setDiscountDialogOpen(false);
              setSelectedProduct(null);
            }}
            product={selectedProduct}
          />
          <ProductDetailsModal
            open={detailsOpen}
            onClose={() => {
              setDetailsOpen(false);
              setDetailsProduct(null);
            }}
            product={detailsProduct}
            currency={currency}
            onEdit={() => {
              setDetailsOpen(false);
              handleModify(detailsProduct);
            }}
            onDelete={() => {
              setDetailsOpen(false);
              handleClickOpen(detailsProduct._id);
            }}
            onDiscount={() => {
              setDetailsOpen(false);
              handleDiscount(detailsProduct);
            }}
          />
          <TablePagination
            rowsPerPageOptions={[12, 24, 48]}
            component="div"
            count={totalCount}
            rowsPerPage={limit}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </main>
    </div>
  );
};

export default ProductsList;
